import { z } from 'zod'

// Shared pieces every document uses. Dates are ISO strings so the documents survive JSON as they are.

export const IsoDateTime = z.string().describe('ISO 8601 date-time, UTC, e.g. 2026-10-08T09:30:00Z')
export const IsoDuration = z.string().describe('ISO 8601 duration, e.g. PT5S, PT20S, P3D')

export const Environment = z.enum(['uat', 'production'])
export type Environment = z.infer<typeof Environment>

export const Role = z.enum(['admin', 'editor', 'reviewer', 'analyst'])
export type Role = z.infer<typeof Role>

export const Permission = z.enum([
  'workflows.view',
  'workflows.edit',
  'workflows.review',
  'workflows.publish',
  'runs.view',
  'runs.execute',
  'connectors.view',
  'connectors.manage',
  'tables.view',
  'tables.edit',
  'tables.manage',
  'bindings.edit',
  'bindings.publish',
  'alerts.view',
  'alerts.ack',
  'settings.manage',
  'users.manage',
  'audit.view',
  'packs.view',
  'code.edit',
  'code.publish',
])
export type Permission = z.infer<typeof Permission>

/** The permission list of plan §4 (K7): what each role may do inside a workspace. */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  admin: Permission.options,
  editor: [
    'workflows.view',
    'workflows.edit',
    'runs.view',
    'runs.execute',
    'connectors.view',
    'tables.view',
    'tables.edit',
    'bindings.edit',
    'alerts.view',
    'alerts.ack',
    'audit.view',
    'packs.view',
    'code.edit',
  ],
  reviewer: [
    'workflows.view',
    'workflows.review',
    'runs.view',
    'connectors.view',
    'tables.view',
    'alerts.view',
    'audit.view',
    'packs.view',
  ],
  analyst: ['workflows.view', 'runs.view', 'connectors.view', 'tables.view', 'alerts.view', 'packs.view'],
}

/** A JSON Schema document (draft 2020-12) kept as plain JSON: node card inputs, manifest action inputs. */
export const JsonSchemaDocument = z.record(z.string(), z.unknown()).describe('A JSON Schema document')
export type JsonSchemaDocument = z.infer<typeof JsonSchemaDocument>

export const JsonValue: z.ZodType<unknown> = z.unknown()

/** A page of rows. */
export const PageInfo = z.object({
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(500),
  total: z.number().int().min(0),
})
export type PageInfo = z.infer<typeof PageInfo>
